const destinations = [
  {
    name: "Caribbean (Western)",
    context: "Cozumel, Roatan, Grand Cayman, and Belize are the top Western Caribbean stops for Port Tampa Bay Carnival sailings. Tampa families book these with 2–4 weeks notice during promotional windows",
    urgency: "Boarding documents arrive 1–2 weeks before sailing — passport discoveries at this stage require emergency processing",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Mexico",
    context: "Cancun and the Riviera Maya draw Tampa Bay families and spring breakers departing from TPA. Cozumel is also a Port Tampa Bay cruise stop. Mexico requires valid passports for all U.S. citizens entering by air",
    urgency: "Spring break bookings from USF and UT students surge in January — standard processing cannot meet March dates",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Middle East (Military TDY)",
    context: "USCENTCOM and USSOCOM at MacDill routinely assign personnel to Qatar, Kuwait, UAE, Jordan, and Saudi Arabia. TDY travel requires current passports with sufficient validity for in-country requirements plus re-entry",
    urgency: "TDY orders often arrive with 30 days or less — dependent passports must also be valid for accompanied assignments",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "United Kingdom & Europe",
    context: "London, Frankfurt, Amsterdam, and Madrid draw Tampa's finance and tech sector for corporate meetings, banking conferences, and FinTech events. Raymond James and Citigroup executives travel frequently to European offices",
    urgency: "Finance industry Q4 planning cycles create October–November travel surges with 2–3 week booking windows",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Canada",
    context: "Toronto and Vancouver draw Tampa Bay business travelers for financial services conferences and tech industry partnerships. Canadian border entry requires a passport for all U.S. air travelers",
    urgency: "Business trips to Canada often confirm 10–14 days before departure — passport validity is frequently overlooked until booking",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Colombia & Latin America",
    context: "Tampa's significant Cuban-American, Venezuelan, and Colombian communities generate family visit travel to Bogota, Medellin, Caracas, and Havana. Family emergencies can require travel within 48–72 hours",
    urgency: "Family emergencies across Latin America require same-day or next-day processing — we accommodate these situations",
    icon: "ri-map-pin-2-line",
  },
];

const stats = [
  { stat: "58%", label: "of Tampa expedited requests involve military families, cruise departures, or corporate travelers" },
  { stat: "2–4 Weeks", label: "average booking-to-departure window for Tampa Bay international travelers" },
  { stat: "Jan–Mar", label: "peak spring break and cruise season that drives January–February passport urgency" },
  { stat: "30 Days", label: "typical MacDill AFB TDY notice window — barely enough time for standard processing" },
];

export default function TampaDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Tampa Bay Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            From MacDill military TDY orders to Port Tampa Bay cruise departures and downtown finance executive travel, Tampa Bay's diverse traveler base creates multiple distinct passport urgency patterns throughout the year.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Florida Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Tampa Bay and all of Florida with the same expedited passport expertise.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/st-petersburg" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                St. Petersburg <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/clearwater" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Clearwater <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/orlando" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Orlando <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}